import React, { useState, useCallback } from 'react';
import { searchFranchisees } from '../services/franchiseeService';
import { debounce } from 'lodash';
import { FaHashtag, FaSearch, FaTimes, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';
import AuditRunsList from '../components/AuditRunsList';

// Assuming we will add this to utilityService.js
import { updateFseCodeSystemWide } from '../services/utilityService'; 

const useSearch = (searchFunction) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const debouncedSearch = useCallback(
        debounce(async (term) => {
            if (term.trim().length < 3) {
                setSuggestions([]);
                setIsSearching(false);
                setShowSuggestions(false);
                return;
            }

            setIsSearching(true);
            try {
                const data = await searchFunction({ searchString: term, pageSize: 10 });
                setSuggestions(data.franchisees || []);
                setShowSuggestions(true);
            } catch (error) {
                console.error('Search error:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            } finally {
                setIsSearching(false);
            }
        }, 300),
        [searchFunction]
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (value.trim().length >= 3) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
        debouncedSearch(value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSuggestions([]);
        setShowSuggestions(false);
    };

    return {
        searchTerm,
        suggestions,
        isSearching,
        showSuggestions,
        setShowSuggestions,
        handleSearchChange,
        clearSearch
    };
};

const MassFseCodeUpdateUtility = ({ onBack }) => {
    const [selectedFranchisee, setSelectedFranchisee] = useState(null);
    const [newFseCode, setNewFseCode] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [updateResult, setUpdateResult] = useState(null);
    const [error, setError] = useState(null);

    const search = useSearch(searchFranchisees);

    const handleSelectFranchisee = (franchisee) => {
        setSelectedFranchisee(franchisee);
        search.clearSearch();
        setUpdateResult(null);
        setError(null);
        setNewFseCode('');
    };

    const handleUpdateClick = () => {
        if (!selectedFranchisee) {
            setError('Please select a franchisee first.');
            return;
        }
        if (!newFseCode || newFseCode.trim() === '') {
            setError('Please enter a new FSE code.');
            return;
        }
        if (newFseCode.trim() === selectedFranchisee.fseCode) {
            setError('The new FSE code must be different from the current one.');
            return;
        }
        
        setError(null);
        setShowConfirmModal(true);
    };

    const handleConfirmUpdate = async () => {
        try {
            setIsUpdating(true);
            setError(null);
            
            // Execute the system-wide update
            const response = await updateFseCodeSystemWide({
                franchiseeId: selectedFranchisee.id,
                oldFseCode: selectedFranchisee.fseCode,
                newFseCode: newFseCode.trim()
            });

            if (response.status === 'success') {
                setUpdateResult(response.data);
                // Update local state to reflect the new code
                setSelectedFranchisee(prev => ({ ...prev, fseCode: newFseCode.trim() }));
                setNewFseCode('');
            } else {
                setError(response.message || 'Failed to update FSE code.');
            }
        } catch (err) {
            console.error('Update FSE code error:', err);
            setError(err.message || 'An unexpected error occurred during the update.');
        } finally {
            setIsUpdating(false);
            setShowConfirmModal(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-center justify-between px-4 sm:px-0">
                <h1 className="text-2xl font-semibold text-gray-900">System FSE Code Update</h1>
                <button
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                    ← Back to Utilities
                </button>
            </div>

            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 mb-8">
                <p className="text-gray-600 mb-6 font-medium">
                    This utility updates a franchise's FSE Code across all historical Jobs, Invoices, Franchisee Invoices, and RCTIs. 
                    <span className="text-red-600 ml-1">Use with caution.</span>
                </p>

                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <FaExclamationTriangle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {updateResult && (
                    <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <FaCheckCircle className="h-5 w-5 text-green-400" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">Update Successful</h3>
                                <div className="mt-2 text-sm text-green-700">
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>Jobs Updated: {updateResult.jobsUpdated}</li>
                                        <li>Invoices Updated: {updateResult.invoicesUpdated}</li>
                                        <li>Franchisee Invoices Updated: {updateResult.franchiseeInvoicesUpdated}</li>
                                        <li>RCTIs Updated: {updateResult.rctisUpdated}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    {/* Franchisee Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Franchisee</label>
                        {!selectedFranchisee ? (
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    {search.isSearching ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jims-green"></div>
                                    ) : (
                                        <FaSearch className="h-4 w-4 text-gray-400" />
                                    )}
                                </div>
                                <input
                                    type="text"
                                    className="focus:ring-jims-green focus:border-jims-green block w-full pl-10 sm:text-sm border-gray-300 rounded-md py-2"
                                    placeholder="Search by name, fse code, or region..."
                                    value={search.searchTerm}
                                    onChange={(e) => search.handleSearchChange(e.target.value)}
                                    onFocus={() => search.searchTerm.length >= 3 && search.setShowSuggestions(true)}
                                />
                                {search.searchTerm && (
                                    <button
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        onClick={search.clearSearch}
                                    >
                                        <FaTimes className="h-4 w-4" />
                                    </button>
                                )}

                                {/* Search Suggestions */}
                                {search.showSuggestions && search.suggestions.length > 0 && (
                                    <div className="absolute z-10 mt-1 flex w-full flex-col max-h-60 overflow-y-auto bg-white shadow-lg rounded-md border border-gray-200">
                                        {search.suggestions.map((franchisee) => (
                                            <button
                                                key={franchisee.id}
                                                className="flex flex-col text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 border-b border-gray-100 last:border-0"
                                                onClick={() => handleSelectFranchisee(franchisee)}
                                            >
                                                <span className="font-medium text-gray-900">{franchisee.franchiseName}</span>
                                                <span className="text-sm text-gray-500">
                                                    Code: {franchisee.fseCode} | Region: {franchisee.region}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md">
                                <div>
                                    <h4 className="text-lg font-medium text-gray-900">{selectedFranchisee.franchiseName}</h4>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Current FSE Code: <span className="font-bold text-gray-700">{selectedFranchisee.fseCode}</span>
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedFranchisee(null)}
                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                >
                                    Change Selection
                                </button>
                            </div>
                        )}
                    </div>

                    {/* New FSE Code Input */}
                    {selectedFranchisee && (
                        <div className="border-t border-gray-200 pt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">New FSE Code</label>
                            <div className="max-w-xs">
                                <input
                                    type="text"
                                    className="focus:ring-jims-green focus:border-jims-green block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                                    placeholder="Enter new FSE code..."
                                    value={newFseCode}
                                    onChange={(e) => setNewFseCode(e.target.value)}
                                />
                            </div>
                            
                            <div className="mt-6">
                                <button
                                    onClick={handleUpdateClick}
                                    disabled={!newFseCode || newFseCode.trim() === selectedFranchisee.fseCode || isUpdating}
                                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-jims-green hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-green ${(!newFseCode || newFseCode.trim() === selectedFranchisee.fseCode || isUpdating) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <FaHashtag className="mr-2 -ml-1 h-4 w-4" />
                                    Update FSE Code System-Wide
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <AuditRunsList operation="update-fse-code" title="Recent FSE Code Updates" />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmUpdate}
                title="Confirm System-Wide FSE Code Update"
                message={
                    <div>
                        <div className="flex items-center mb-4 text-red-600">
                            <FaExclamationTriangle className="mr-2 h-6 w-6" />
                            <span className="font-bold text-lg">WARNING</span>
                        </div>
                        <p className="mb-4 text-gray-700">
                            You are about to permanently alter historical and relational data for <strong>{selectedFranchisee?.franchiseName}</strong>. 
                        </p>
                        <div className="bg-red-50 p-4 rounded-md mb-4 border border-red-200">
                            <div className="mb-2 text-red-800">
                                <strong>From:</strong> {selectedFranchisee?.fseCode}
                            </div>
                            <div className="text-red-800">
                                <strong>To:</strong> {newFseCode.trim()}
                            </div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                            <p className="text-sm text-yellow-800 font-bold mb-2">
                                The following records will be modified to use the new FSE code:
                            </p>
                            <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                                <li>The Franchise Profile</li>
                                <li>All associated Jobs</li>
                                <li>All Customer Invoices</li>
                                <li>All Franchisee Admin Invoices</li>
                                <li>All RCTIs</li>
                            </ul>
                        </div>
                        <p className="text-sm text-gray-600 font-medium">
                            This action cannot be undone. Are you sure you want to proceed?
                        </p>
                    </div>
                }
                isLoading={isUpdating}
                loadingText="Updating FSE codes across the system..."
                confirmText="Yes, Update Everywhere"
                confirmColor="red"
            />
        </div>
    );
};

export default MassFseCodeUpdateUtility;
